import { addMinutes, subYears } from 'date-fns';
import { seededRandom } from 'lib_ts/classes/math.utilities';
import { OutcomeCode } from 'lib_ts/enums/hitters.enums';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';

const DEMO_MACHINE_ID = 'demo-machine';

/* ---------------------------------------------------------------- *
 * Session recap field and strike zone
 *
 * Plate locations cluster by pitch type around the strike zone, with a
 * chase ring and a few waste pitches. Batted balls favour pull-side
 * ground balls and line drives. videoIDs are real annotated videos
 * assigned at random so the pitch card can play one.
 * ---------------------------------------------------------------- */

export const DEMO_RECAP_COUNT = 100;

/** RHP targets, feet. +x is toward 1B. LHP flips x. */
const DEMO_RECAP_TARGETS: {
  type: PitchType;
  weight: number;
  x: number;
  z: number;
  speed: number;
  xBreak: number;
  zBreak: number;
}[] = [
  {
    type: PitchType.FourSeamFastball,
    weight: 0.36,
    x: -0.12,
    z: 2.95,
    speed: 94,
    xBreak: -4,
    zBreak: 16,
  },
  {
    type: PitchType.Sinker,
    weight: 0.14,
    x: -0.4,
    z: 2.05,
    speed: 93,
    xBreak: -12,
    zBreak: 6,
  },
  {
    type: PitchType.Slider,
    weight: 0.18,
    x: 0.42,
    z: 1.85,
    speed: 85,
    xBreak: 6,
    zBreak: 2,
  },
  {
    type: PitchType.ChangeUp,
    weight: 0.12,
    x: -0.28,
    z: 1.9,
    speed: 85,
    xBreak: -10,
    zBreak: 8,
  },
  {
    type: PitchType.Curveball,
    weight: 0.1,
    x: 0.08,
    z: 1.7,
    speed: 79,
    xBreak: 6,
    zBreak: -8,
  },
  {
    type: PitchType.Cutter,
    weight: 0.1,
    x: 0.28,
    z: 2.7,
    speed: 89,
    xBreak: 2,
    zBreak: 10,
  },
];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const gaussian = (random: () => number) => {
  let u = 0;
  let v = 0;

  while (u === 0) {
    u = random();
  }

  while (v === 0) {
    v = random();
  }

  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

const pickWeighted = <T extends { weight: number }>(
  random: () => number,
  items: T[]
): T => {
  let cursor = random() * items.reduce((sum, item) => sum + item.weight, 0);

  for (const item of items) {
    cursor -= item.weight;

    if (cursor <= 0) {
      return item;
    }
  }

  return items[items.length - 1];
};

export const generateDemoSessionHits = (
  seed: number,
  videoIDs: string[],
  count: number
): ISessionHit[] => {
  const random = seededRandom(seed);

  const startDate = subYears(new Date(), 1);

  const hits: ISessionHit[] = [
    // ? spawn hits at each of the bases to check that the field SVG is correctly positioned and scaled
    {
      _id: `demo-recap-${seed}-home`,
      _created: startDate.toISOString(),
      _changed: startDate.toISOString(),
      avg_excluded: false,
      tracking_device: TrackingDevice.TrajektVision,
      fire: {
        _id: `demo-recap-fire-${seed}-home`,
        machineID: DEMO_MACHINE_ID,
        session: 'demo-recap-session',
        annotated_video_id: videoIDs[0],
      },
      pitch: {
        _id: `demo-recap-pitch-${seed}-home`,
        type: PitchType.FourSeamFastball,
        name: 'Demo Pitch',
        speedMPH: 90,
        plate: { plate_x: 0, plate_z: 2.5 },
        release: { px: -1, py: 54, pz: 5 },
      },
      hit: {
        invalid: false,
        swing: true,
        _mx_swing: true,
        vLaunchDEG: 0,
        hLaunchDEG: 0,
        hLandingDEG: 0,
        distanceFT: 0,
      },
    },
    {
      _id: `demo-recap-${seed}-first`,
      _created: startDate.toISOString(),
      _changed: startDate.toISOString(),
      avg_excluded: false,
      tracking_device: TrackingDevice.TrajektVision,
      fire: {
        _id: `demo-recap-fire-${seed}-first`,
        machineID: DEMO_MACHINE_ID,
        session: 'demo-recap-session',
        annotated_video_id: videoIDs[0],
      },
      pitch: {
        _id: `demo-recap-pitch-${seed}-first`,
        type: PitchType.FourSeamFastball,
        name: 'Demo Pitch',
        speedMPH: 90,
        plate: { plate_x: 0, plate_z: 2.5 },
        release: { px: -1, py: 54, pz: 5 },
      },
      hit: {
        invalid: false,
        swing: true,
        _mx_swing: true,
        vLaunchDEG: 0,
        hLaunchDEG: 0,
        hLandingDEG: 45,
        distanceFT: 90,
      },
    },
    {
      _id: `demo-recap-${seed}-second`,
      _created: startDate.toISOString(),
      _changed: startDate.toISOString(),
      avg_excluded: false,
      tracking_device: TrackingDevice.TrajektVision,
      fire: {
        _id: `demo-recap-fire-${seed}-second`,
        machineID: DEMO_MACHINE_ID,
        session: 'demo-recap-session',
        annotated_video_id: videoIDs[0],
      },
      pitch: {
        _id: `demo-recap-pitch-${seed}-second`,
        type: PitchType.FourSeamFastball,
        name: 'Demo Pitch',
        speedMPH: 90,
        plate: { plate_x: 0, plate_z: 2.5 },
        release: { px: -1, py: 54, pz: 5 },
      },
      hit: {
        invalid: false,
        swing: true,
        _mx_swing: true,
        vLaunchDEG: 0,
        hLaunchDEG: 0,
        hLandingDEG: 0,
        distanceFT: 128,
      },
    },
    {
      _id: `demo-recap-${seed}-third`,
      _created: startDate.toISOString(),
      _changed: startDate.toISOString(),
      avg_excluded: false,
      tracking_device: TrackingDevice.TrajektVision,
      fire: {
        _id: `demo-recap-fire-${seed}-third`,
        machineID: DEMO_MACHINE_ID,
        session: 'demo-recap-session',
        annotated_video_id: videoIDs[0],
      },
      pitch: {
        _id: `demo-recap-pitch-${seed}-third`,
        type: PitchType.FourSeamFastball,
        name: 'Demo Pitch',
        speedMPH: 90,
        plate: { plate_x: 0, plate_z: 2.5 },
        release: { px: -1, py: 54, pz: 5 },
      },
      hit: {
        invalid: false,
        swing: true,
        _mx_swing: true,
        vLaunchDEG: 0,
        hLaunchDEG: 0,
        hLandingDEG: -45,
        distanceFT: 90,
      },
    },
  ];

  let i = hits.length;

  while (i < count) {
    // each "hit" is a minute apart
    const created = addMinutes(startDate, i).toISOString();

    const pitch = pickWeighted(random, DEMO_RECAP_TARGETS);
    const lhp = random() < 0.28;
    const xSign = lhp ? -1 : 1;
    const spread = random() < 0.62 ? 0.22 : random() < 0.88 ? 0.45 : 0.8;
    const plate_x = clamp(
      pitch.x * xSign + gaussian(random) * spread,
      -2.1,
      2.1
    );
    const plate_z = clamp(pitch.z + gaussian(random) * spread * 0.85, 0.5, 4.5);
    const inZone =
      Math.abs(plate_x) <= 0.83 && plate_z >= 1.6 && plate_z <= 3.4;
    const chase =
      Math.abs(plate_x) <= 1.35 && plate_z >= 1.15 && plate_z <= 3.95;
    const swing = random() < (inZone ? 0.68 : chase ? 0.3 : 0.08);
    const contact = swing && random() < (inZone ? 0.78 : 0.52);

    let vLaunchDEG: number | undefined;
    let exitMPH: number | undefined;
    let hLandingDEG: number | undefined;
    let distanceFT: number | undefined;
    let outcome: OutcomeCode | undefined;

    if (contact) {
      const batted = pickWeighted(random, [
        {
          weight: 0.46,
          la: 1,
          ev: 86,
          laSigma: 6,
          evSigma: 7,
        },
        {
          weight: 0.27,
          la: 16,
          ev: 93,
          laSigma: 4,
          evSigma: 6,
        },
        {
          weight: 0.2,
          la: 32,
          ev: 91,
          laSigma: 6,
          evSigma: 7,
        },
        {
          weight: 0.07,
          la: 55,
          ev: 78,
          laSigma: 7,
          evSigma: 5,
        },
      ]);

      vLaunchDEG = clamp(
        batted.la + gaussian(random) * batted.laSigma,
        -25,
        70
      );
      exitMPH = clamp(batted.ev + gaussian(random) * batted.evSigma, 55, 112);

      const carry =
        vLaunchDEG < 0
          ? 0.4
          : vLaunchDEG > 48
            ? 0.45
            : 0.7 + (1 - Math.min(Math.abs(vLaunchDEG - 28) / 36, 1)) * 0.85;

      distanceFT = clamp(
        exitMPH * 2.15 * carry + gaussian(random) * 12,
        8,
        440
      );

      const foul = random() < (vLaunchDEG > 42 ? 0.22 : 0.28);
      // RHH pull is toward 3B, which is a negative spray angle
      hLandingDEG = foul
        ? (random() < 0.62 ? -1 : 1) * (46 + random() * 18)
        : clamp(-12 + gaussian(random) * 16, -44, 44);

      if (Math.abs(hLandingDEG) > 45) {
        outcome = OutcomeCode.Foul;
      } else if (
        exitMPH >= 100 &&
        vLaunchDEG >= 18 &&
        vLaunchDEG <= 35 &&
        distanceFT >= 360 &&
        random() < 0.7
      ) {
        outcome = OutcomeCode.HomeRun;
        distanceFT = clamp(distanceFT, 370, 440);
      } else if (
        distanceFT >= 320 &&
        vLaunchDEG >= 12 &&
        vLaunchDEG <= 24 &&
        random() < 0.04
      ) {
        outcome = OutcomeCode.Triple;
      } else if (distanceFT >= 250 && vLaunchDEG >= 8 && random() < 0.28) {
        outcome = OutcomeCode.Double;
      } else if (distanceFT >= 140 && vLaunchDEG < 22 && random() < 0.34) {
        outcome = OutcomeCode.Single;
      } else if (vLaunchDEG >= 25) {
        outcome = OutcomeCode.FlyOut;
      } else {
        outcome = OutcomeCode.GroundOut;
      }
    }

    const videoIndex = Math.floor(random() * Math.max(videoIDs.length, 1));
    const videoID = videoIDs[videoIndex];

    hits.push({
      _id: `demo-recap-${seed}-${i}`,
      _created: created,
      _changed: created,
      avg_excluded: false,
      tracking_device: TrackingDevice.TrajektVision,
      fire: {
        _id: `demo-recap-fire-${seed}-${i}`,
        machineID: DEMO_MACHINE_ID,
        session: 'demo-recap-session',
        annotated_video_id: videoID,
      },
      pitch: {
        _id: `demo-recap-pitch-${seed}-${i}`,
        type: pitch.type,
        name: 'Demo Pitch',
        speedMPH: clamp(pitch.speed + gaussian(random) * 1.4, 72, 101),
        xBreakIN: pitch.xBreak * xSign + gaussian(random) * 1.5,
        zBreakIN: pitch.zBreak + gaussian(random) * 1.5,
        plate: { plate_x, plate_z },
        release: { px: lhp ? 1.6 : -1.6, py: 54, pz: 5.8 },
      },
      hit: {
        invalid: false,
        swing: swing,
        _mx_swing: swing,
        exitMPH: exitMPH,
        vLaunchDEG: vLaunchDEG,
        hLaunchDEG: hLandingDEG,
        hLandingDEG: hLandingDEG,
        distanceFT: distanceFT,
        outcome: outcome,
        _mx_outcome: outcome,
      },
    });

    i++;
  }

  return hits;
};
